/**
 * The signed-in session of this browser.
 *
 * hg-auth keeps a web app's refresh token in a cookie no script can read
 * (`HttpOnly`, sent only to its own refresh and sign-out endpoints). What is
 * left for the page to hold, and this file is the only place that does:
 *
 * - The access token (15 minutes) lives in a variable, nowhere else. A reload
 *   forgets it; the first request after a reload asks for a new one, and the
 *   browser sends the cookie by itself.
 * - The session's id lives in a variable and in `localStorage`. It is not a
 *   secret: hg-auth asks for it with every refresh and sign-out, to tell this
 *   app's own cookie from one somebody planted. Holding it is what "signed
 *   in" means here; whether the cookie still stands, only hg-auth can say.
 * - Tabs of the same browser share the cookie and the id by themselves. Over
 *   a `BroadcastChannel` (memory only) they also hand each other the access
 *   token, so a new tab opens without asking, and they hear a sign-out at
 *   once.
 *
 * Nothing outside `shared/auth` and the API client reads a token.
 */
import type { components } from "@/shared/api/auth.schema";

/** What a sign-in or a refresh answers with, as far as a web app is sent it. */
export type Tokens = Pick<
  components["schemas"]["TokenResponse"],
  "access_token" | "expires_in" | "session_id"
>;

interface Snapshot {
  accessToken: string;
  /** When the access token stops working, epoch milliseconds. */
  expiresAt: number;
  sessionId: string;
}

type Message =
  | { type: "hello" }
  | ({ type: "session" } & Snapshot)
  | { type: "ended"; reason: string | null };

const SESSION_KEY = "hg.cc.session";
/** Where the refresh token itself was kept before hg-auth took it into its cookie. */
const RETIRED_KEY = "hg.cc.refresh";
const CHANNEL = "hg.cc.session";

forgetRetired();

let sessionId: string | null = readStored();
let access: { token: string; expiresAt: number } | null = null;
let endReason: string | null = null;
let channel: BroadcastChannel | null = null;
const listeners = new Set<() => void>();

/** A session is held: the person signed in and has not been signed out. */
export function hasSession(): boolean {
  return sessionId !== null;
}

/** The id hg-auth knows the session by; not a secret. */
export function getSessionId(): string | null {
  return sessionId;
}

export function getAccessToken(): string | null {
  return access?.token ?? null;
}

/** Milliseconds the access token still has; 0 or less when there is none. */
export function accessExpiresIn(): number {
  return access ? access.expiresAt - Date.now() : 0;
}

/** Why the last session ended, when it was not the person's own sign-out. */
export function getEndReason(): string | null {
  return endReason;
}

/** Tokens from a sign-in or a refresh. */
export function startSession(tokens: Tokens): void {
  const snapshot: Snapshot = {
    accessToken: tokens.access_token,
    expiresAt: Date.now() + tokens.expires_in * 1000,
    sessionId: tokens.session_id,
  };
  adopt(snapshot);
  post({ type: "session", ...snapshot });
}

/** Forgets the session in every tab. `reason` is shown on the sign-in page. */
export function endSession(reason: string | null = null): void {
  if (!hasSession() && access === null) return;
  clear(reason);
  post({ type: "ended", reason });
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Connects this tab to the others and asks them for the access token.
 * Resolves as soon as one hands it over, or after `waitMs` when no tab
 * answers. Call once, before the first render: a new tab then opens without
 * a refresh of its own.
 */
export function joinTabs(waitMs = 150): Promise<void> {
  if (channel !== null || typeof BroadcastChannel === "undefined") return Promise.resolve();
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event: MessageEvent<unknown>) => {
    receive(event.data);
  };
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      unsubscribe();
      resolve();
    };
    const timer = setTimeout(finish, waitMs);
    const unsubscribe = subscribeSession(() => {
      if (access !== null) finish();
    });
    post({ type: "hello" });
  });
}

function receive(data: unknown): void {
  if (!isMessage(data)) return;
  switch (data.type) {
    case "hello":
      // Only a tab that holds a token answers.
      if (sessionId !== null && access !== null) {
        post({
          type: "session",
          accessToken: access.token,
          expiresAt: access.expiresAt,
          sessionId,
        });
      }
      return;
    case "session":
      // Another session, or a newer token of this one. Two tabs that refreshed
      // at once each keep the later of the two.
      if (data.sessionId !== sessionId || access === null || data.expiresAt > access.expiresAt) {
        adopt(data);
      }
      return;
    case "ended":
      if (hasSession()) clear(data.reason);
      return;
  }
}

function adopt(snapshot: Snapshot): void {
  sessionId = snapshot.sessionId;
  access = { token: snapshot.accessToken, expiresAt: snapshot.expiresAt };
  endReason = null;
  writeStored(sessionId);
  notify();
}

function clear(reason: string | null): void {
  sessionId = null;
  access = null;
  endReason = reason;
  writeStored(null);
  notify();
}

function notify(): void {
  for (const listener of listeners) listener();
}

function post(message: Message): void {
  channel?.postMessage(message);
}

function isMessage(data: unknown): data is Message {
  if (typeof data !== "object" || data === null || !("type" in data)) return false;
  const message = data as Partial<Snapshot> & { type: unknown; reason?: unknown };
  switch (message.type) {
    case "hello":
      return true;
    case "ended":
      return message.reason === null || typeof message.reason === "string";
    case "session":
      return (
        typeof message.accessToken === "string" &&
        typeof message.expiresAt === "number" &&
        typeof message.sessionId === "string"
      );
    default:
      return false;
  }
}

function readStored(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(SESSION_KEY);
    else localStorage.setItem(SESSION_KEY, id);
  } catch {
    // Storage is switched off: the session still works, a reload ends it.
  }
}

/** A refresh token an older build left in this tab's storage has no business staying there. */
function forgetRetired(): void {
  try {
    sessionStorage.removeItem(RETIRED_KEY);
  } catch {
    // Nothing was kept where nothing can be read.
  }
}
